import type { IRegDataComparisonFieldCard, IRegSystemOption } from "../../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { WasteReturnRegFieldKeys } from "../../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import type { ComparatorContext, FieldActionPolicy } from "./reconciliationGridTypes";
import { getBoolOrFalse } from "./regDataBool";

export interface FieldComparatorConfig {
    fieldKey: string;
    context: ComparatorContext;
    hasFlags: boolean;
    fieldPolicy: FieldActionPolicy;
    flagsAgree(templateParsed: Record<string, unknown>, systemOption: IRegSystemOption): boolean;
}

const DEFAULT_POLICY: FieldActionPolicy = {
    allowAdd: true,
    allowSoftDelete: true,
    allowRestore: true,
};

/** True when any template raw value carries an asterisk immediately after the six-digit EWC code. */
export function isEwcHazardDeterminate(card: IRegDataComparisonFieldCard): boolean {
    const sources = card.matches?.map((m) => m.templateOption.rawValue) ?? card.templateOptions?.map((t) => t.rawValue) ?? [];
    return sources.some((raw) => ewcRawValueHasDeterminateHazard(raw));
}

export function ewcRawValueHasDeterminateHazard(raw: string): boolean {
    const compact = raw.trim().replace(/\s+/g, "");
    const match = /^(\d{6})/.exec(compact);
    if (!match) return false;
    return compact.length > 6 && compact[6] === "*";
}

function getBool(parsed: Record<string, unknown>, key: string): boolean {
    return getBoolOrFalse(parsed, key);
}

function getSystemBool(option: IRegSystemOption, key: string): boolean {
    return getBoolOrFalse(option.structured, key);
}

function ewcFlagsAgree(templateParsed: Record<string, unknown>, systemOption: IRegSystemOption, hazardDeterminate: boolean): boolean {
    if (!hazardDeterminate) return true;
    return getBool(templateParsed, "isHazardous") === getSystemBool(systemOption, "isHazardous");
}

function eaDrFlagsAgree(templateParsed: Record<string, unknown>, systemOption: IRegSystemOption): boolean {
    return (
        getBool(templateParsed, "isForReceipt") === getSystemBool(systemOption, "isForReceipt") &&
        getBool(templateParsed, "isForRemoval") === getSystemBool(systemOption, "isForRemoval")
    );
}

export function buildFieldComparatorConfig(card: IRegDataComparisonFieldCard): FieldComparatorConfig {
    const fieldKey = card.fieldKey;
    const fieldPolicy = DEFAULT_POLICY;

    if (fieldKey === WasteReturnRegFieldKeys.Ewc) {
        const hazardDeterminate = isEwcHazardDeterminate(card);
        return {
            fieldKey,
            context: { ewcHazardDeterminate: hazardDeterminate },
            hasFlags: true,
            fieldPolicy,
            flagsAgree: (templateParsed, systemOption) => ewcFlagsAgree(templateParsed, systemOption, hazardDeterminate),
        };
    }

    if (fieldKey === WasteReturnRegFieldKeys.EaDr) {
        return {
            fieldKey,
            context: {},
            hasFlags: true,
            fieldPolicy,
            flagsAgree: eaDrFlagsAgree,
        };
    }

    return {
        fieldKey,
        context: {},
        hasFlags: false,
        fieldPolicy,
        flagsAgree: () => true,
    };
}
